// interfaces/SessionService.ts
import Cookies from "js-cookie"
import { FranchiseRole } from "../models/enums/franchiserole"
import { Terminal, User, Franchise } from "../models/user"
import { VendorProduct } from "models/product"
import { GetOrderResponse } from "models/response/GetOrdersResponse"


/**
 * Manages user session data within the application.
 */
export interface SessionService {
  /**
   * Checks if user is logged in or not by checking the cookie.
   */
  isLoggedin: boolean
  /**
   * Gets the current user from the session.
   * @returns The user object if logged in, otherwise null.
   */
  user: User | null

  /**
   * List of terminals that user has access to.
   * @returns The terminals list object if available, otherwise null.
   */
  terminals: Terminal[]

  /**
   * Updates an existing terminal in the list of terminals.
   *
   * @param updatedTerminal - The terminal object with updated details.
   *
   * This method finds the terminal in the `terminals` array with the same `terminalId`
   * as the provided `updatedTerminal` and replaces it with the new object.
   * If no terminals exist, the function returns early.
   */
  updateTerminal(updatedTerminal: Terminal): void

  /**
   * Gets or sets the list of franchise users in the session.
   * @returns The array of franchise users if available, otherwise null.
   */
  franchiseUsers: User[] | null

  /**
  * Updates an existing franchise user in the list of franchise users.
  *
  * @param updatedUser - The user object with updated details.
  *
  * This method finds the user in the `franchiseUsers` array with the same `userId`
  * as the provided `updatedUser` and replaces it with the new object.
  * If no franchise users exist or the user isn't found, the function returns early.
  */
  updateFranchiseUser(updatedUser: User): void

  /**
   * Stores and retrieves the franchise name.
   * @returns The franchise name if available, otherwise null.
   */
  franchiseName: string | null

  /**
   * Stores and retrieves the entire franchise details.
   * @returns The franchise object if available, otherwise null.
   */
  franchiseDetails: Franchise | null
  
  /** 
   * SessionStorage-backed orders. Returns parsed orders array 
   * or null if no stored data exists for ordersKey.
   */
  orders: GetOrderResponse[] | null

  /** 
   * Persists orders to SessionStorage. Stores as JSON string if non-null, 
   * removes SessionStorage entry if null is provided.
   */
  setOrders(orders: GetOrderResponse[] | null): void

  /**
   * Checks if the currently logged-in user has a specific role.
   * @param requiredRole The role to check.
   * @returns True if the user has the required role, false otherwise.
   */
  hasRole(requiredRole: FranchiseRole): boolean

  /**
   * Determines if the user can access restricted UI elements.
   * Typically, only admins and moderators should have this access.
   * @returns True if the user has permission, false otherwise.
   */
  canCreateUser(): boolean

  /**
   * Logs out the current user and clears the session.
   */
  resetStorage(): void

  /**
   * Retrieves the vendor product from session storage.
   *
   * @returns {VendorProduct | null} The stored vendor product object, or `null` if not found.
   *
   * This getter fetches the vendor product data from `sessionStorage`, parses it,
   * and returns the corresponding `VendorProduct` object. If no data exists, it returns `null`.
   */
  metadata: any | null

  /**
   * Property to hold vendor product data.
   * 
   * @type {VendorProduct | null}
   */
  vendorProduct: VendorProduct | null
}

export class AppSessionService implements SessionService {
  private userKey = "appUser"
  private terminalsKey = "terminals"
  private franchiseKey = "franchiseDetails" 
  private franchiseUsersKey = "franchiseUsers"
  private vendorProductKey = "vendorProduct"
  private metadataKey = "metadata"
  private ordersKey = "orders"

  // Setter for franchise details
  set franchiseDetails(franchise: Franchise | null) {
    if (franchise) {
      sessionStorage.setItem(this.franchiseKey, JSON.stringify(franchise))
    } else {
      sessionStorage.removeItem(this.franchiseKey)
    }
  }

  // Getter for franchise details
  get franchiseDetails(): Franchise | null {
    const data = sessionStorage.getItem(this.franchiseKey)
    return data ? JSON.parse(data) : null
  }

  // Setter for franchise name 
  set franchiseName(franchise: Franchise | string | null) {
    if (typeof franchise === "string") {
      sessionStorage.setItem(
        this.franchiseKey,
        JSON.stringify({ name: franchise })
      )
    } else if (franchise && typeof franchise === "object") {
      sessionStorage.setItem(this.franchiseKey, JSON.stringify(franchise))
    } else {
      sessionStorage.removeItem(this.franchiseKey)
    }
  }

  // Getter for franchise name (backward compatibility)
  get franchiseName(): string | null {
    const franchise = this.franchiseDetails
    return franchise ? franchise.name : null
  }

  get isLoggedin(): boolean {
    const token = Cookies.get("token")
    return token != null
  }

  // Getter for user session data
  get user(): User | null {
    const data = sessionStorage.getItem(this.userKey)
    return data ? JSON.parse(data) : null
  }

  // Setter for user session
  set user(user: User | null) {
    if (user) {
      sessionStorage.setItem(this.userKey, JSON.stringify(user))
    } else {
      sessionStorage.removeItem(this.userKey)
    }
  }

  get terminals(): Terminal[] {
    const data = sessionStorage.getItem(this.terminalsKey)
    return data ? JSON.parse(data) : null
  }

  set terminals(terminals: Terminal[] | null) {
    if (terminals) {
      sessionStorage.setItem(this.terminalsKey, JSON.stringify(terminals))
    } else {
      sessionStorage.removeItem(this.terminalsKey)
    }
  }

  // Getter for franchise users
  get franchiseUsers(): User[] | null {
    const data = sessionStorage.getItem(this.franchiseUsersKey)
    return data ? JSON.parse(data) : null
  }

  // Setter for franchise users
  set franchiseUsers(users: User[] | null) {
    if (users) {
      sessionStorage.setItem(this.franchiseUsersKey, JSON.stringify(users))
    } else {
      sessionStorage.removeItem(this.franchiseUsersKey)
    }
  }

  get orders(): GetOrderResponse[] | null {
    const data = sessionStorage.getItem(this.ordersKey)
    return data ? JSON.parse(data) : null
  }

  setOrders(orders: GetOrderResponse[] | null) {
    if (orders) {
      sessionStorage.setItem(this.ordersKey, JSON.stringify(orders))
    } else {
      sessionStorage.removeItem(this.ordersKey)
    }
  }

  // Checks if the user has a specific role
  hasRole(requiredRole: FranchiseRole): boolean {
    return this.user?.role === requiredRole
  }

  // Getter for vendor product
  get vendorProduct(): VendorProduct | null {
    const data = sessionStorage.getItem(this.vendorProductKey)
    return data ? JSON.parse(data) : null
  }

  // Setter for vendor product
  set vendorProduct(product: VendorProduct | null) {
    if (product) {
      sessionStorage.setItem(this.vendorProductKey, JSON.stringify(product))
    } else {
      sessionStorage.removeItem(this.vendorProductKey)
    }
  }

  // Determines if the user can access restricted UI elements
  canCreateUser(): boolean {
    return (
      this.user?.role === FranchiseRole.ADMIN ||
      this.user?.role === FranchiseRole.OWNER
    )
  }

  updateTerminal(updatedTerminal: Terminal) {
    const terminals = this.terminals
    if (!terminals) return

    const updatedTerminals = terminals.map((t) =>
      t.terminalId === updatedTerminal.terminalId ? updatedTerminal : t
    )

    this.terminals = updatedTerminals
  }

  updateFranchiseUser(updatedUser: User): void {
    let franchiseUsers = this.franchiseUsers || []


    // Check if the user already exists in the franchiseUsers array
    const userIndex = franchiseUsers.findIndex(
      (user) => user.email === updatedUser.email
    )

    if (userIndex !== -1) {
      // If the user exists, update their data (including terminals)
      franchiseUsers[userIndex] = {
        ...franchiseUsers[userIndex],
        ...updatedUser,
        terminals: updatedUser.terminals || franchiseUsers[userIndex].terminals,
      }
    } else {
      // If the user does not exist, add them to the array
      franchiseUsers.push(updatedUser)
    }

    // Update the session with the new franchiseUsers array
    this.franchiseUsers = franchiseUsers

    // Log the updated franchiseUsers for debugging
    console.log("Updated franchiseUsers:", franchiseUsers)
  }

  // Getter for metadata
  get metadata(): any | null {
    const data = sessionStorage.getItem(this.metadataKey)
    return data ? JSON.parse(data) : null
  }

  // Setter for metadata
  set metadata(value: any | null) {
    if (value) {
      sessionStorage.setItem(this.metadataKey, JSON.stringify(value))
    } else {
      sessionStorage.removeItem(this.metadataKey)
    }
  }

  // Logs out the user by clearing sessionStorage
  resetStorage() {
    this.user = null
    this.terminals = null
    this.franchiseDetails = null
    this.franchiseUsers = null
    this.metadata = null
    this.vendorProduct = null
    Cookies.remove("token")
  }
}
